import React, { useState, useEffect } from 'react';
import { FaUser, FaSpinner, FaExclamationCircle, FaReceipt, FaCalendarAlt, FaChevronRight, FaMoneyBillWave } from 'react-icons/fa';
import { Receipt, Calendar, CreditCard, ArrowDown, Clock, CheckCircle, AlertCircle, XCircle, Search } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const DepositRecord = ({ userDetails, token }) => {
  const [deposits, setDeposits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    status: 'all'
  });

  useEffect(() => {
    const fetchDeposits = async () => {
      try {
        const phoneNumber = userDetails.mobile;
        const response = await axios.get(
          `https://api.visiontech2030.com/api/deposits/${phoneNumber}`,
          { headers: { 'Authorization': token } }
        );
        setDeposits(response.data);
      } catch (err) {
        setError('Failed to load deposit records');
        console.error('Error fetching deposits:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeposits();
  }, [userDetails.mobile, token]);

  const getStatusIcon = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'completed' || statusLower === 'success') {
      return <CheckCircle size={16} className="vt-deposit-status-icon success" />;
    } else if (statusLower === 'pending') {
      return <Clock size={16} className="vt-deposit-status-icon pending" />;
    } else if (statusLower === 'failed') {
      return <XCircle size={16} className="vt-deposit-status-icon failed" />;
    } else {
      return <AlertCircle size={16} className="vt-deposit-status-icon unknown" />;
    }
  };

  const filteredDeposits = deposits.filter(deposit => {
    const matchesSearch = search === '' || 
      deposit.mpesaReceiptNumber.toLowerCase().includes(search.toLowerCase()) ||
      deposit.amount.toString().includes(search);
    
    const matchesStatus = activeFilters.status === 'all' || 
      deposit.status.toLowerCase() === activeFilters.status;
    
    return matchesSearch && matchesStatus;
  });

  const calculateTotalDeposits = () => {
    return deposits.reduce((total, deposit) => {
      if (deposit.status.toLowerCase() === 'completed' || deposit.status.toLowerCase() === 'success') {
        return total + deposit.amount;
      }
      return total;
    }, 0);
  };

  const EmptyState = () => (
    <div className="vt-deposit-empty">
      <div className="vt-deposit-empty-icon">
        <Receipt size={40} />
      </div>
      <h3 className="vt-deposit-empty-title">No Deposit Records</h3>
      <p className="vt-deposit-empty-message">
        You haven't made any deposits yet. Start investing to see your transaction history here.
      </p>
    </div>
  );

  const LoadingState = () => (
    <div className="vt-deposit-loading">
      <div className="vt-deposit-spinner"></div>
      <p className="vt-deposit-loading-text">Loading your deposit records...</p>
    </div>
  );

  const ErrorState = () => (
    <div className="vt-deposit-error">
      <div className="vt-deposit-error-icon">
        <AlertCircle size={32} />
      </div>
      <h3 className="vt-deposit-error-title">Could Not Load Records</h3>
      <p className="vt-deposit-error-message">{error}</p>
      <button className="vt-deposit-retry-button">Try Again</button>
    </div>
  );

  return (
    <div className="vt-deposit-container">
      <div className="vt-deposit-header">
        <div className="vt-deposit-header-decoration"></div>
        <div className="vt-deposit-header-icon">
          <Receipt size={20} />
        </div>
        <div className="vt-deposit-header-content">
          <h2 className="vt-deposit-title">Transaction History</h2>
          <p className="vt-deposit-subtitle">Track all your deposit activities</p>
        </div>
      </div>
      
      {!isLoading && !error && deposits.length > 0 && (
        <div className="vt-deposit-summary-card">
          <div className="vt-deposit-summary-item">
            <div className="vt-deposit-summary-label">Total Deposits</div>
            <div className="vt-deposit-summary-value">
              KES {calculateTotalDeposits().toLocaleString()}
            </div>
          </div>
          <div className="vt-deposit-summary-item">
            <div className="vt-deposit-summary-label">Transactions</div>
            <div className="vt-deposit-summary-value">{deposits.length}</div>
          </div>
        </div>
      )}

      {!isLoading && !error && deposits.length > 0 && (
        <div className="vt-deposit-filters">
          <div className="vt-deposit-search">
            <Search size={16} className="vt-deposit-search-icon" />
            <input
              type="text"
              placeholder="Search by receipt number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="vt-deposit-search-input"
            />
          </div>
          
          <div className="vt-deposit-status-filters">
            <button 
              className={`vt-deposit-status-filter ${activeFilters.status === 'all' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'all' }))}
            >
              All
            </button>
            <button 
              className={`vt-deposit-status-filter ${activeFilters.status === 'completed' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'completed' }))}
            >
              Completed
            </button>
            <button 
              className={`vt-deposit-status-filter ${activeFilters.status === 'pending' ? 'active' : ''}`} 
              onClick={() => setActiveFilters(prev => ({ ...prev, status: 'pending' }))}
            >
              Pending
            </button>
          </div>
        </div>
      )}

      <div className="vt-deposit-content">
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState />
        ) : deposits.length === 0 ? (
          <EmptyState />
        ) : filteredDeposits.length === 0 ? (
          <div className="vt-deposit-no-results">
            <p>No results found for your search criteria</p>
          </div>
        ) : (
          <div className="vt-deposit-list">
            {filteredDeposits.map((deposit) => (
              <div key={deposit.id} className="vt-deposit-card">
                <div className="vt-deposit-card-main">
                  <div className="vt-deposit-icon-wrapper">
                    <ArrowDown 
                      size={18} 
                      className="vt-deposit-transaction-icon" 
                    />
                  </div>
                  
                  <div className="vt-deposit-info">
                    <div className="vt-deposit-amount-row">
                      <span className="vt-deposit-amount">
                        <span className="vt-deposit-currency">KES</span>
                        {' '}
                        {deposit.amount.toLocaleString()}
                      </span>
                      <div className="vt-deposit-status">
                        {getStatusIcon(deposit.status)}
                        <span className={`vt-deposit-status-text ${deposit.status.toLowerCase()}`}>
                          {deposit.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="vt-deposit-details-row">
                      <div className="vt-deposit-detail">
                        <Receipt size={14} className="vt-deposit-detail-icon" />
                        <span className="vt-deposit-receipt-number">
                          {deposit.mpesaReceiptNumber}
                        </span>
                      </div>
                      <div className="vt-deposit-detail">
                        <Calendar size={14} className="vt-deposit-detail-icon" />
                        <span className="vt-deposit-date">
                          {format(new Date(deposit.transactionDate), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="vt-deposit-detail">
                        <Clock size={14} className="vt-deposit-detail-icon" />
                        <span className="vt-deposit-time">
                          {format(new Date(deposit.transactionDate), 'h:mm a')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="vt-deposit-chevron">
                    <FaChevronRight />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <style jsx>{`
        .vt-deposit-container {
          max-width: 800px;
          margin: 0 auto;
        }
        
        .vt-deposit-header {
          display: flex;
          align-items: center;
          gap: 16px;
          position: relative;
          margin-bottom: 24px;
        }
        
        .vt-deposit-header-decoration {
          position: absolute;
          left: -20px;
          top: -10px;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(79, 70, 229, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          z-index: -1;
        }
        
        .vt-deposit-header-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: white;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(79, 70, 229, 0.3);
          flex-shrink: 0;
        }
        
        .vt-deposit-header-content {
          flex: 1;
        }
        
        .vt-deposit-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 4px;
          color: white;
        }
        
        .vt-deposit-subtitle {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        .vt-deposit-summary-card {
          display: flex;
          gap: 20px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
        }
        
        .vt-deposit-summary-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .vt-deposit-summary-label {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-deposit-summary-value {
          font-size: 18px;
          font-weight: 600;
          color: white;
        }
        
        .vt-deposit-filters {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .vt-deposit-search {
          position: relative;
        }
        
        .vt-deposit-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.5);
        }
        
        .vt-deposit-search-input {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 12px 12px 12px 40px;
          color: white;
          font-size: 14px;
          outline: none;
          transition: all 0.2s ease;
        }
        
        .vt-deposit-search-input:focus {
          border-color: rgba(79, 70, 229, 0.4);
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
        }
        
        .vt-deposit-search-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        
        .vt-deposit-status-filters {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 4px;
        }
        
        .vt-deposit-status-filter {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.7);
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        
        .vt-deposit-status-filter.active {
          background: rgba(79, 70, 229, 0.2);
          border-color: rgba(79, 70, 229, 0.4);
          color: #a5b4fc;
        }
        
        .vt-deposit-status-filter:hover:not(.active) {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .vt-deposit-content {
          min-height: 200px;
        }
        
        .vt-deposit-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .vt-deposit-card {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          overflow: hidden;
          transition: all 0.2s ease;
        }
        
        .vt-deposit-card:hover {
          transform: translateY(-2px);
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .vt-deposit-card-main {
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .vt-deposit-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(79, 70, 229, 0.1);
          border-radius: 10px;
          color: #a5b4fc;
          flex-shrink: 0;
        }
        
        .vt-deposit-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .vt-deposit-amount-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .vt-deposit-amount {
          font-size: 16px;
          font-weight: 600;
          color: white;
        }
        
        .vt-deposit-currency {
          color: rgba(255, 255, 255, 0.7);
          font-weight: normal;
          font-size: 14px;
        }
        
        .vt-deposit-status {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .vt-deposit-status-icon.success {
          color: #34d399;
        }
        
        .vt-deposit-status-icon.pending {
          color: #fbbf24;
        }
        
        .vt-deposit-status-icon.failed {
          color: #f87171;
        }
        
        .vt-deposit-status-icon.unknown {
          color: #a1a1aa;
        }
        
        .vt-deposit-status-text {
          font-size: 13px;
          font-weight: 500;
        }
        
        .vt-deposit-status-text.completed,
        .vt-deposit-status-text.success {
          color: #34d399;
        }
        
        .vt-deposit-status-text.pending {
          color: #fbbf24;
        }
        
        .vt-deposit-status-text.failed {
          color: #f87171;
        }
        
        .vt-deposit-details-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        
        .vt-deposit-detail {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .vt-deposit-detail-icon {
          color: rgba(255, 255, 255, 0.5);
        }
        
        .vt-deposit-receipt-number,
        .vt-deposit-date,
        .vt-deposit-time {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-deposit-chevron {
          color: rgba(255, 255, 255, 0.3);
          font-size: 12px;
        }
        
        /* Empty State */
        .vt-deposit-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 60px 20px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          border: 1px dashed rgba(255, 255, 255, 0.1);
        }
        
        .vt-deposit-empty-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 70px;
          height: 70px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
          color: rgba(255, 255, 255, 0.3);
          margin-bottom: 16px;
        }
        
        .vt-deposit-empty-title {
          font-size: 18px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 8px;
        }
        
        .vt-deposit-empty-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          max-width: 300px;
          margin: 0;
          line-height: 1.5;
        }
        
        /* Loading State */
        .vt-deposit-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
        }
        
        .vt-deposit-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(79, 70, 229, 0.1);
          border-top: 3px solid #4f46e5;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .vt-deposit-loading-text {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        /* Error State */
        .vt-deposit-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 60px 20px;
          background: rgba(239, 68, 68, 0.05);
          border-radius: 12px;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        
        .vt-deposit-error-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 50%;
          color: #f87171;
          margin-bottom: 16px;
        }
        
        .vt-deposit-error-title {
          font-size: 18px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 8px;
        }
        
        .vt-deposit-error-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          margin: 0 0 16px;
        }
        
        .vt-deposit-retry-button {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .vt-deposit-retry-button:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px);
        }
        
        .vt-deposit-no-results {
          display: flex;
          justify-content: center;
          padding: 40px 0;
          color: rgba(255, 255, 255, 0.6);
          font-size: 15px;
        }
        
        /* Responsive adjustments */
        @media (max-width: 600px) {
          .vt-deposit-details-row {
            flex-direction: column;
            gap: 6px;
          }
          
          .vt-deposit-summary-card {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default DepositRecord;